import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./Header.css";
import logo from "../../assets/aviatrickslogo.svg";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1024);
  const headerRef = useRef(null);

  /* ---------- RESPONSIVE ---------- */
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- SCROLL ---------- */
  useEffect(() => {
    const handleScroll = () => {
      if (!isMobileView) {
        headerRef.current?.classList.toggle("compact", window.scrollY > 60);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobileView]);

  /* ---------- LOCK SCROLL ---------- */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  /* ---------- HELPERS ---------- */
  const closeAll = () => {
    setMenuOpen(false);
    setActiveDropdown(null);
    setActiveAccordion(null);
  };

  const handleMouseEnter = (name) => {
    if (!isMobileView) setActiveDropdown(name);
  };
  const handleMouseLeave = () => {
    if (!isMobileView) setActiveDropdown(null);
  };
  const toggleAccordion = (name) => {
    setActiveAccordion((prev) => (prev === name ? null : name));
  };

  /* ---------- DATA ---------- */
  const solutionColumns = [
    {
      heading: "Design",
      comingSoon: false,
      items: [
        { name: "Airframe", path: "/design/airframe" },
      ],
    },
    {
      heading: "Hardware",
      comingSoon: false,
      items: [
        { name: "Flight Controller", path: "/hardware/fc" },
      ],
    },
    {
      heading: "Software",
      comingSoon: false,
      items: [
        { name: "Agri", path: "/our-businesses/agri" },
        { name: "Survey", path: "/our-businesses/survey" },
        { name: "Camera", path: "/our-businesses/camera" },
        { name: "Overview", path: "/our-businesses/overview" },
      ],
    },
  ];

  const platformLinks = [
    { name: "Flytutor", path: "/businesses/flytutor" },
    { name: "Flyhub", path: "/businesses/flyhub" },
    { name: "Airspacemap", path: "/businesses/airspacemap" },
    // { name: "SolarMark", path: "/businesses/solarmark" },
    { name: "Agri Simulation", path: "/businesses/agri-sim" },
  ];

  const contactLinks = [
    { name: "Help & Support", path: "/contact/support" },
    { name: "Become a Dealer", path: "/contact/franchise" },
    { name: "Partner With Us", path: "/contact/partners" },
  ];

  return (
    <header className="header" ref={headerRef}>
      <div className="header-inner">
        {/* LOGO */}
        <Link to="/" className="logo" onClick={closeAll}>
          <img src={logo} alt="Aviatricks" />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="nav-desktop">
          <Link to="/" className="nav-link-plain">Home</Link>
          <Link to="/about" className="nav-link-plain">About Us</Link>
          <Link to="/store" className="nav-link-plain">Store</Link>

          {/* SOLUTIONS – mega menu */}
          <div
            className={`dropdown-wrap ${activeDropdown === "solutions" ? "active" : ""}`}
            onMouseEnter={() => handleMouseEnter("solutions")}
            onMouseLeave={handleMouseLeave}
          >
            <button className="nav-trigger">
              Our Solutions <FiChevronDown className="trigger-icon" />
            </button>
            <div className="mega-menu">
              {solutionColumns.map((col) => (
                <div key={col.heading} className={`mega-col ${col.comingSoon ? "col-disabled" : ""}`}>
                  <p className="mega-heading">
                    {col.heading}
                    {col.comingSoon && <span className="badge">Soon</span>}
                  </p>
                  {col.items.map((item) => (
                    <Link key={item.path} to={item.path} className="mega-link" onClick={closeAll}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* PLATFORMS – simple dropdown */}
          <div
            className={`dropdown-wrap ${activeDropdown === "platforms" ? "active" : ""}`}
            onMouseEnter={() => handleMouseEnter("platforms")}
            onMouseLeave={handleMouseLeave}
          >
            <button className="nav-trigger">
              Our Platforms <FiChevronDown className="trigger-icon" />
            </button>
            <div className="simple-menu">
              {platformLinks.map((link) => (
                <Link key={link.path} to={link.path} className="simple-link" onClick={closeAll}>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* CONTACT – simple dropdown */}
          <div
            className={`dropdown-wrap ${activeDropdown === "contact" ? "active" : ""}`}
            onMouseEnter={() => handleMouseEnter("contact")}
            onMouseLeave={handleMouseLeave}
          >
            <button className="nav-trigger">
              Contact <FiChevronDown className="trigger-icon" />
            </button>
            <div className="simple-menu">
              {contactLinks.map((link) => (
                <Link key={link.path} to={link.path} className="simple-link" onClick={closeAll}>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* DESKTOP AUTH */}
        {/* <div className="header-actions">
          <Link to="/login" className="btn-login">Log In</Link>
          <Link to="/register" className="btn-register">Get Started</Link>
        </div> */}

        {/* MOBILE HAMBURGER */}
        <button
          className={`menu-toggle ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {/* ===== MOBILE OVERLAY ===== */}
      <div className={`mobile-overlay ${menuOpen ? "visible" : ""}`} onClick={closeAll} />

      <div className={`mobile-panel ${menuOpen ? "open" : ""}`}>
        {/* Panel header */}
        <div className="mobile-panel-header">
          <Link to="/" className="logo" onClick={closeAll}>
            <img src={logo} alt="Aviatricks" />
          </Link>
          <button className="panel-close" onClick={closeAll}><FiX /></button>
        </div>

        {/* Panel nav */}
        <nav className="mobile-nav">
          <Link to="/" className="mobile-nav-item" onClick={closeAll}>Home</Link>
          <Link to="/about" className="mobile-nav-item" onClick={closeAll}>About Us</Link>
          <Link to="/store" className="mobile-nav-item" onClick={closeAll}>Store</Link>

          {/* SOLUTIONS accordion */}
          <div className="mobile-accordion">
            <button
              className="mobile-nav-item accordion-trigger"
              onClick={() => toggleAccordion("solutions")}
            >
              Our Solutions
              <FiChevronDown className={`acc-icon ${activeAccordion === "solutions" ? "rotated" : ""}`} />
            </button>
            <div className={`accordion-body ${activeAccordion === "solutions" ? "open" : ""}`}>
              {solutionColumns.map((col) => (
                <div key={col.heading} className="acc-group">
                  <p className="acc-group-label">
                    {col.heading}
                    {col.comingSoon && <span className="badge">Soon</span>}
                  </p>
                  {col.items.map((item) => (
                    <Link key={item.path} to={item.path} className="acc-link" onClick={closeAll}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* PLATFORMS accordion */}
          <div className="mobile-accordion">
            <button
              className="mobile-nav-item accordion-trigger"
              onClick={() => toggleAccordion("platforms")}
            >
              Our Platforms
              <FiChevronDown className={`acc-icon ${activeAccordion === "platforms" ? "rotated" : ""}`} />
            </button>
            <div className={`accordion-body ${activeAccordion === "platforms" ? "open" : ""}`}>
              {platformLinks.map((link) => (
                <Link key={link.path} to={link.path} className="acc-link" onClick={closeAll}>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* CONTACT accordion */}
          <div className="mobile-accordion">
            <button
              className="mobile-nav-item accordion-trigger"
              onClick={() => toggleAccordion("contact")}
            >
              Contact
              <FiChevronDown className={`acc-icon ${activeAccordion === "contact" ? "rotated" : ""}`} />
            </button>
            <div className={`accordion-body ${activeAccordion === "contact" ? "open" : ""}`}>
              {contactLinks.map((link) => (
                <Link key={link.path} to={link.path} className="acc-link" onClick={closeAll}>
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Panel footer CTAs */}
        {/* <div className="mobile-panel-footer">
          <Link to="/login" className="mobile-btn-login" onClick={closeAll}>Log In</Link>
          <Link to="/register" className="mobile-btn-register" onClick={closeAll}>Get Started</Link>
        </div> */}
      </div>
    </header>
  );
}